import styled from 'styled-components';
import {
  ContextualMenu,
  ContextualMenuItemType,
  IContextualMenuItem,
  IContextualMenuProps,
} from '@fluentui/react';
import { identity } from 'lodash';
import React from 'react';
import ContextualMenuOption from '../Dropdown/ContextualMenu/ContextualMenuOption';
import { IAliasOption } from './types';
import { IContextualMenuItemGenericData } from './types';
import ContextualMenuSubItemWrapper from '../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import RenameMenu from './RenameMenu';

const ValueContainer = styled.div`
  max-width: 224px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

interface IProps {
  isMenuVisible: boolean;
  menuAnchor: React.MutableRefObject<HTMLElement | null>;
  aliases: IAliasOption[];
  extraMenuItems?: IContextualMenuItem[];
  onDismiss?: IContextualMenuProps['onDismiss'];
  setMenuIsOpen?: (isMenuOpen: boolean) => void;
  // we haven't update all dropdown menus, to keep the consistency of item icons,
  // use this prop to control the display of the menu item icon.
  showRenameIcon?: boolean;
}

/**
 * Add extra menu items after last alias Rename option. Modifies in-place
 * aliasItems
 */
const injectExtraItems = (
  aliasItems: IContextualMenuItemGenericData<{ type?: IAliasOption['type'] }>[],
  extraMenuItems: IContextualMenuItem[],
) => {
  // this is poor man's implementation of Array.findLast (Chrome 97+)
  // not yet available in our version of TypeScript
  let lastAliasIndex: number | undefined;
  for (let i = aliasItems.length - 1; i >= 0; i--) {
    if (aliasItems[i]?.data?.type === 'alias') {
      lastAliasIndex = i;
      break;
    }
  }
  // if not found, add items on top, otherwise, after last 'alias'
  aliasItems.splice(
    lastAliasIndex === undefined ? 0 : lastAliasIndex + 1,
    0,
    ...extraMenuItems,
  );
};

/**
 * Component for creating / removing aliases on Measures, Breakdowns
 *
 * @param isMenuVisible - if true, the context menu for editing aliases is visible
 * @param hideMenu - a callback when the current menu is closed
 * @param menuAnchor - position the menu next to this element
 * @param editorAnchor - position the editor UI next to this element
 * @param aliases - the list of current aliases of this element
 * @param extraMenuItems - additional context items to display in the context menu
 * @param onDismiss - handler for dismissing the whole menu
 */
export default ({
  isMenuVisible,
  menuAnchor,
  aliases,
  extraMenuItems,
  onDismiss,
  setMenuIsOpen,
  showRenameIcon = false,
}: IProps) => {
  const items = aliases.reduce<
    IContextualMenuItemGenericData<Pick<IAliasOption, 'type'>>[]
  >((acc, aliasOption, index) => {
    const type = aliasOption.type;
    if (type === 'jumpTo') {
      acc.push({
        key: aliasOption.name,
        data: { type },
        onRenderContent: () => (
          <ContextualMenuOption
            label={<ValueContainer>{aliasOption.name}</ValueContainer>}
            onClick={aliasOption.jumpToHandler}
          />
        ),
      });
    } else if (type === 'divider') {
      acc.push({
        key: index.toString(),
        itemType: ContextualMenuItemType.Divider,
        data: { type },
      });
    } else if (type === 'alias') {
      acc.push({
        key: aliasOption.userAliasId,
        data: { type },
        onRenderContent: () => (
          <ContextualMenuOption
            iconName={showRenameIcon ? 'pencil' : undefined}
            label={<ValueContainer>Rename</ValueContainer>}
            hasSubMenus={true}
          />
        ),
        subMenuProps: {
          items: [
            {
              key: 'rename',
              label: 'Rename',
              onRender: (_, dismissMenu) => {
                return (
                  <ContextualMenuSubItemWrapper>
                    <RenameMenu
                      aliasOption={aliasOption}
                      dismissMenu={dismissMenu}
                    />
                  </ContextualMenuSubItemWrapper>
                );
              },
            },
          ],
        },
      });
    }
    return acc;
  }, []);

  if (extraMenuItems && extraMenuItems.length > 0) {
    injectExtraItems(items, extraMenuItems);
  }

  return (
    <>
      <ContextualMenu
        styles={{
          root: {
            minWidth: '240px',
          },
        }}
        items={items}
        isBeakVisible={false}
        beakWidth={8}
        gapSpace={4}
        hidden={!isMenuVisible}
        // The onRestoreFocus and shouldFocusOnMount properties are required to avoid stealing and restoring
        // focus from Semantic dropdown
        // Otherwise, when the "Rename" menu is closed, it autoexpands the dropdown and it feels weird
        onRestoreFocus={identity}
        shouldFocusOnMount={false}
        target={menuAnchor}
        alignTargetEdge={true}
        onDismiss={onDismiss}
        onMenuDismissed={() => setMenuIsOpen?.(false)}
        onMenuOpened={() => setMenuIsOpen?.(true)}
      />
    </>
  );
};
